import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileSpreadsheet, FileText, Loader2, Sheet, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/KpiCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/FilterBar";
import { EnregistrerCalcul, HistoriqueCalculs } from "@/components/HistoriqueCalculs";
import { api, errMsg, money } from "@/lib/api";

const num = (v) => Number(String(v).replace(",", ".").replace(/\s/g, ""));
const Choix = ({ value, options, onChange, testId }) => (
  <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid={testId}>
    {options.map(([v, l]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testId}-${v}`}
        className={`rounded-md px-3 py-1.5 text-sm transition-colors ${value === v ? "bg-white font-semibold text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>{l}</button>
    ))}
  </div>
);
const Champ = ({ label, children }) => <label className="space-y-1.5 text-sm"><span className="block text-xs font-medium text-slate-600">{label}</span>{children}</label>;

const libelleMontant = (f) => (f.sens === "inverse"
  ? (f.saisie === "nuit" ? "Total payé par nuit (taxes incluses, $)" : "Total payé pour le séjour (taxes incluses, $)")
  : (f.saisie === "nuit" ? "Prix par nuit ($)" : "Montant total du séjour, sans ménage ni taxes ($)"));

const Formulaire = ({ f, set }) => (
  <Panel title="Réservation" testId="calcul-formulaire">
    <div className="space-y-5">
      <div className="flex flex-wrap gap-4">
        <Choix value={f.plateforme} options={[["Airbnb", "Airbnb"], ["Booking", "Booking"]]} onChange={(v) => set("plateforme", v)} testId="calcul-plateforme" />
        <Choix value={f.sens} options={[["regulier", "Calcul régulier"], ["inverse", "Calcul inversé"]]} onChange={(v) => set("sens", v)} testId="calcul-sens" />
        <Choix value={f.saisie} options={[["nuit", "Prix par nuit × nuits"], ["total", "Montant total du séjour"]]} onChange={(v) => set("saisie", v)} testId="calcul-saisie" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Champ label={libelleMontant(f)}><Input inputMode="decimal" value={f.montant} onChange={(e) => set("montant", e.target.value)} data-testid="calcul-montant" /></Champ>
        <Champ label="Nombre de nuits"><Input inputMode="numeric" value={f.nuits} onChange={(e) => set("nuits", e.target.value)} data-testid="calcul-nuits" /></Champ>
        <Champ label="Frais de ménage ($)"><Input inputMode="decimal" value={f.frais_menage} onChange={(e) => set("frais_menage", e.target.value)} data-testid="calcul-menage" /></Champ>
        <Champ label="Date de réservation (règle de commission)"><Input type="date" value={f.date_reservation} onChange={(e) => set("date_reservation", e.target.value)} data-testid="calcul-date" /></Champ>
        <Champ label="Ajustement du tarif (rabais en % ou en $ pour le séjour, facultatif)">
          <div className="flex gap-2">
            <Input inputMode="decimal" value={f.ajustement} onChange={(e) => set("ajustement", e.target.value)} placeholder="0" data-testid="calcul-ajustement" />
            <Choix value={f.ajustement_type} options={[["pct", "%"], ["montant", "$"]]} onChange={(v) => set("ajustement_type", v)} testId="calcul-ajustement-type" />
          </div>
        </Champ>
      </div>
      <p className="text-xs text-slate-500">
        {f.sens === "inverse" ? "Calcul inversé : à partir du montant payé par le voyageur (ménage et taxes compris), l'application retrouve le loyer exact." : "Calcul régulier : à partir du loyer, l'application calcule les taxes, le total payé par le voyageur, la commission et le montant net."}
        {" "}Exact (Revenu Québec) : taxe d'hébergement 3,5 % sur le loyer (exemptée à partir de 31 nuits) ; TPS 5 % et TVQ 9,975 % sur loyer + ménage + taxe d'hébergement. Le rabais en $ s'applique au séjour.
      </p>
    </div>
  </Panel>
);

const Exports = ({ corps }) => {
  const [busy, setBusy] = useState("");
  const dl = async (format) => {
    setBusy(format);
    try {
      const r = await api.post("/calcul-reservation/export", corps, { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Calcul réservation ${corps.plateforme} ${corps.date_reservation}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const B = ({ format, icon: Icon, label }) => (
    <Button size="sm" variant="outline" className="h-8 bg-white" disabled={!!busy} onClick={() => dl(format)} data-testid={`calcul-export-${format}`}>
      {busy === format ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Icon className="mr-1 h-3.5 w-3.5" />}{label}
    </Button>
  );
  return <div className="flex flex-wrap gap-2"><B format="pdf" icon={FileText} label="PDF" /><B format="csv" icon={Sheet} label="CSV" /><B format="xlsx" icon={FileSpreadsheet} label="Excel" /></div>;
};

const GROUPES = [["voyageur", "Le voyageur a payé"], ["hote", "Vous gagnez"]];

const Ligne = ({ l, reel, m }) => {
  const fort = ["total", "net"].includes(l.cle);
  return (
    <tr className={`border-t border-slate-100 ${fort ? "bg-slate-50 font-semibold" : ""}`} data-testid={`calcul-ligne-${l.cle}`}>
      <td className="w-10 px-3 py-2.5 font-mono text-xs text-slate-500" data-testid={`calcul-lettre-${l.cle}`}>({l.lettre})</td>
      <td className="px-2 py-2.5">{l.libelle}</td>
      <td className="px-2 py-2.5 text-xs font-normal text-slate-500" data-testid={`calcul-base-${l.cle}`}>{l.base}</td>
      <td className="px-3 py-2.5 text-right tabular">{m(l.exact)}</td>
      <td className="px-3 py-2.5 text-right tabular">{m(l.erreur)}</td>
      <td className={`px-3 py-2.5 text-right tabular ${l.ecart ? "text-amber-700" : "text-slate-400"}`}>{m(l.ecart)}</td>
      {reel && <>
        <td className="px-3 py-2.5 text-right tabular text-sky-800" data-testid={`calcul-reel-${l.cle}`}>{m(l.reel)}</td>
        <td className={`px-5 py-2.5 text-right tabular ${l.ecart_reel ? "text-red-700" : "text-slate-400"}`} data-testid={`calcul-ecart-reel-${l.cle}`}>{m(l.ecart_reel)}</td>
      </>}
    </tr>
  );
};

const Resultat = ({ c, corps, onSaved, resa }) => {
  const reel = !!c.reservation;
  const m = (v) => (v == null ? "—" : money(v));
  const nb = reel ? 8 : 6;
  return (
  <Panel title="Résultat" action={<Exports corps={corps} />} testId="calcul-resultat">
    <p className="mb-4 text-xs text-slate-500" data-testid="calcul-erreur-libelle">Erreur habituelle {corps.plateforme} : {c.erreur_libelle}. Écart de taxes : <b className="text-slate-900">{money(c.ecart_taxes)}</b>.
      {reel && <> Écart exact − réel : <b className="text-slate-900" data-testid="calcul-ecart-reel">{money(c.ecart_reel_taxes)}</b>.</>}</p>
    <div className="-mx-5 overflow-x-auto">
      <table className={`w-full text-sm ${reel ? "min-w-[1150px]" : "min-w-[920px]"}`} data-testid="calcul-table">
        <thead><tr className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
          <th className="px-3 py-2" /><th className="px-2 py-2 text-left">Élément</th><th className="px-2 py-2 text-left">Base de calcul</th>
          <th className="px-3 py-2 text-right">Calcul exact (Revenu Québec)</th><th className="px-3 py-2 text-right">Calcul {corps.plateforme} (avec erreur)</th><th className="px-3 py-2 text-right">Écart</th>
          {reel && <><th className="px-3 py-2 text-right" data-testid="calcul-col-reel">Réel (réservation)</th><th className="px-5 py-2 text-right">Exact − réel</th></>}
        </tr></thead>
        {GROUPES.map(([g, titre]) => (
          <tbody key={g} data-testid={`calcul-groupe-${g}`}>
            <tr className="border-t-2 border-slate-200"><td colSpan={nb} className="px-3 pb-1 pt-4 text-xs font-semibold uppercase tracking-wider text-emerald-800">{titre}</td></tr>
            {c.lignes.filter((l) => l.groupe === g).map((l) => <Ligne key={l.cle} l={l} reel={reel} m={m} />)}
          </tbody>
        ))}
      </table>
    </div>
    {reel && <p className="mt-3 text-xs text-slate-500">Réel : taxes versées par la plateforme, taxe d'hébergement remise ou perçue par la plateforme (non versée à l'hôte), commission de la réservation.</p>}
    <div className="mt-4 border-t border-slate-100 pt-4"><EnregistrerCalcul key={JSON.stringify(resa || {})} corps={corps} onSaved={onSaved} defauts={resa} /></div>
  </Panel>
  );
};

const Bandeau = ({ r, onRetirer }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900" data-testid="calcul-reservation-bandeau">
    <span>{r.code ? <>Comparé à la réservation <b className="font-mono">{r.code}</b></> : "Calcul rouvert"}{r.nom_client ? ` · ${r.nom_client}` : ""}{r.date_debut ? ` · ${r.date_debut} → ${r.date_fin}` : ""}{r.logement ? ` · ${r.logement}` : ""}</span>
    <Button size="sm" variant="ghost" className="ml-auto h-7 text-sky-900" onClick={onRetirer} data-testid="calcul-retirer-reservation"><X className="mr-1 h-3.5 w-3.5" />Retirer</Button>
  </div>
);

export default function CalculReservation() {
  const [f, setF] = useState(() => { const d = new Date(); return { plateforme: "Airbnb", sens: "regulier", saisie: "nuit", montant: "100", nuits: "2", frais_menage: "50", ajustement: "", ajustement_type: "pct", date_reservation: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` }; });
  const [c, setC] = useState(null);
  const [params, setParams] = useSearchParams();
  const rid = params.get("reservation");
  const [resa, setResa] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => {
    if (!rid) { return; }
    api.get(`/calcul-reservation/depuis/${rid}`).then(({ data }) => {
      const { code, nom_client, date_debut, date_fin, logement, ...champs } = data;
      setF({ ...champs, montant: String(champs.montant), nuits: String(champs.nuits), frais_menage: String(champs.frais_menage), ajustement: "" });
      setResa({ id: rid, code, nom_client, date_debut, date_fin, logement });
      setInfo(null);
      setOnglet("calculer");
    }).catch((e) => { toast.error(errMsg(e)); setParams({}); });
  }, [rid]); // eslint-disable-line react-hooks/exhaustive-deps
  const [info, setInfo] = useState(null);
  const retirer = () => { setResa(null); setInfo(null); setParams({}); setF(({ reservation_id, ...p }) => p); };
  const [historique, setHistorique] = useState(null);
  const [onglet, setOnglet] = useState("calculer");
  const chargerHistorique = () => { api.get("/calculs-reservation").then((r) => setHistorique(r.data)).catch((e) => toast.error(errMsg(e))); };
  useEffect(() => { chargerHistorique(); }, []);
  const ouvrir = async (h) => {
    const { reservation_id: lien, ...e } = h.entree;
    let reservation_id = lien;
    if (reservation_id) {
      try { await api.get(`/calcul-reservation/depuis/${reservation_id}`); } catch (err) {
        if (err.response?.status !== 404) { toast.error(errMsg(err)); return; }
        reservation_id = undefined;
      }
    }
    setParams({});
    setF({ ...e, montant: String(e.montant), nuits: String(e.nuits), frais_menage: String(e.frais_menage), ajustement: e.ajustement ? String(e.ajustement) : "", ajustement_type: e.ajustement_type || "pct" });
    const r = h.resume;
    setResa(reservation_id ? { id: reservation_id, code: r.code, nom_client: r.nom_client, date_debut: r.date_debut, date_fin: r.date_fin, logement: r.logement } : null);
    setInfo(!reservation_id && (r.nom_client || r.date_debut || r.logement) ? { nom_client: r.nom_client, date_debut: r.date_debut, date_fin: r.date_fin, logement: r.logement } : null);
    setOnglet("calculer");
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (lien && !reservation_id) { toast.info("La réservation liée a été supprimée : calcul rouvert sans la colonne Réel"); } else { toast.success(`Calcul « ${h.nom || h.created_at.slice(0, 10)} » rouvert`); }
  };
  const corps = { ...f, montant: num(f.montant), nuits: parseInt(f.nuits, 10), frais_menage: num(f.frais_menage || 0), ajustement: num(f.ajustement || 0), reservation_id: resa ? resa.id : undefined };
  const valide = corps.montant > 0 && corps.nuits >= 1 && corps.frais_menage >= 0 && corps.ajustement >= 0 && !(corps.ajustement_type === "pct" && corps.ajustement >= 100) && /^\d{4}-\d{2}-\d{2}$/.test(f.date_reservation);
  const cle = JSON.stringify(corps);
  useEffect(() => {
    if (!valide) { setC(null); return undefined; }
    let actif = true;
    const t = setTimeout(() => { api.post("/calcul-reservation", corps).then((r) => { if (actif) { setC(r.data); } }).catch((e) => { if (actif) { toast.error(errMsg(e)); } }); }, 300);
    return () => { actif = false; clearTimeout(t); };
  }, [cle]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Outils" title="Calculer une réservation" />
      <Tabs value={onglet} onValueChange={setOnglet} className="space-y-6">
        <TabsList data-testid="calcul-onglets">
          <TabsTrigger value="calculer" data-testid="calcul-onglet-calculer">Calculer</TabsTrigger>
          <TabsTrigger value="historique" data-testid="calcul-onglet-historique">Historique{historique ? ` (${historique.length})` : ""}</TabsTrigger>
        </TabsList>
        <TabsContent value="calculer" className="space-y-6">
          {(resa || info) && <Bandeau r={resa || info} onRetirer={retirer} />}
          <Formulaire f={f} set={set} />
          {c ? <Resultat c={c} corps={corps} onSaved={chargerHistorique} resa={resa || info} /> : <p className="text-sm text-slate-500" data-testid="calcul-invalide">Entrez un montant, un nombre de nuits et une date valides.</p>}
        </TabsContent>
        <TabsContent value="historique">
          <HistoriqueCalculs liste={historique} onOuvrir={ouvrir} onChange={chargerHistorique} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
